import React, { useState } from 'react';
import Iconify from 'src/components/Iconify';
import { CANVAS_VARIAVEIS } from './canvasUtils';
import { DOCUMENTO_DESIGN as D } from '../../EditorDocumento.design';

const ELEMENTOS_BASICOS = [
  { tipo: 'texto', label: 'Texto', icon: 'mdi:format-text', descricao: 'Bloco de texto formatado' },
  { tipo: 'imagem', label: 'Imagem', icon: 'mdi:image-outline', descricao: 'Logo, foto ou ilustração' },
  { tipo: 'retangulo', label: 'Retângulo', icon: 'mdi:rectangle-outline', descricao: 'Forma de fundo ou destaque' },
  { tipo: 'linha', label: 'Linha', icon: 'mdi:minus', descricao: 'Divisor horizontal' },
];

// Blocos dinâmicos pré-configurados — mesmo catálogo do editor de blocos
// (blockTypes.js), preenchidos automaticamente com os dados da proposta.
const BLOCOS_PROPOSTA = [
  { tipo: 'data', label: 'Data', icon: 'mdi:calendar-outline', descricao: 'Data da proposta' },
  { tipo: 'validade', label: 'Validade', icon: 'mdi:calendar-clock-outline', descricao: 'Prazo de validade' },
  { tipo: 'dados_cliente', label: 'Dados do cliente', icon: 'mdi:account-outline', descricao: 'Nome, e-mail e telefone' },
  { tipo: 'forma_pagamento', label: 'Forma de pagamento', icon: 'mdi:credit-card-outline', descricao: 'Condições de pagamento' },
  { tipo: 'itens', label: 'Tabela de itens', icon: 'mdi:table-large', descricao: 'Itens do orçamento', unique: true },
];

function ItemPaleta({ item, disabled, onDragStart, onClick }) {
  return (
    <button
      type="button"
      disabled={disabled}
      draggable={!disabled}
      onDragStart={onDragStart}
      onClick={onClick}
      className={D.paletteItem}
      title={disabled ? 'Já adicionado' : `${item.descricao}. Arraste para a página ou clique para inserir.`}
    >
      <div className={D.paletteIcon}>
        <Iconify icon={item.icon} width={16} />
      </div>
      <div className="min-w-0">
        <p className={D.paletteItemTitle}>{item.label}</p>
        <p className={D.paletteItemDesc}>{disabled ? 'Já adicionado' : item.descricao}</p>
      </div>
    </button>
  );
}

// Paleta lateral esquerda do editor canvas: inserir elementos, variáveis dinâmicas e
// páginas (as camadas ficam na sidebar direita — LayersSidebar). Itens são arrastáveis
// (drag-and-drop) para posicionar exatamente onde o usuário soltar na página; o
// clique continua funcionando como atalho (insere centralizado).
export default function ElementsSidebar({ onAddElemento, onAddVariavel, temItens, paginas, paginaAtiva, onSelectPagina, onAddPagina, onRemovePagina }) {
  const [showVariaveis, setShowVariaveis] = useState(false);

  const handleDragStart = (e, tipo) => {
    e.dataTransfer.setData('application/x-proposta-elemento', tipo);
    e.dataTransfer.effectAllowed = 'copy';
  };

  return (
    <div className="p-3 space-y-5">
      <div>
        <p className={D.paletteTitle}>Elementos</p>
        <div className="space-y-0.5">
          {ELEMENTOS_BASICOS.map(el => (
            <ItemPaleta key={el.tipo} item={el} onDragStart={e => handleDragStart(e, el.tipo)} onClick={() => onAddElemento(el.tipo)} />
          ))}
        </div>
      </div>

      <div>
        <p className={D.paletteTitle}>Blocos da proposta</p>
        <div className="space-y-0.5">
          {BLOCOS_PROPOSTA.map(el => (
            <ItemPaleta
              key={el.tipo}
              item={el}
              disabled={el.unique && temItens}
              onDragStart={e => handleDragStart(e, el.tipo)}
              onClick={() => onAddElemento(el.tipo)}
            />
          ))}
        </div>
      </div>

      <div>
        <button
          type="button"
          onClick={() => setShowVariaveis(v => !v)}
          className="w-full flex items-center justify-between px-1 mb-2"
          aria-expanded={showVariaveis}
        >
          <span className={`${D.paletteTitle} mb-0 px-0`}>Variáveis</span>
          <Iconify icon={showVariaveis ? 'mdi:chevron-up' : 'mdi:chevron-down'} width={16} className="text-gray-400" />
        </button>
        {showVariaveis && (
          <>
            <p className={`${D.paletteHint} mb-2`}>Preenchidas com os dados da proposta na hora de enviar.</p>
            <div className="flex flex-wrap gap-1.5 px-1">
              {CANVAS_VARIAVEIS.map(v => (
                <button
                  key={v.chave}
                  type="button"
                  onClick={() => onAddVariavel(v.chave)}
                  title={v.label}
                  className="text-[11px] font-mono px-2 py-1 rounded-md bg-[#F1EAFE] dark:bg-violet-500/10 text-[#5B21B6] dark:text-violet-300 hover:bg-[#EADDFD] dark:hover:bg-violet-500/20 transition-colors"
                >
                  {`{{${v.chave}}}`}
                </button>
              ))}
            </div>
          </>
        )}
      </div>

      <div className="pt-4 border-t border-gray-200/70 dark:border-[#383838]">
        <p className={D.paletteTitle}>Páginas</p>
        <div className="space-y-0.5">
          {paginas.map((_, i) => (
            <div key={i} className="flex items-center gap-1 group">
              <button
                type="button"
                onClick={() => onSelectPagina(i)}
                aria-current={paginaAtiva === i ? 'true' : undefined}
                className={`flex-1 flex items-center gap-2 px-2 py-1.5 rounded-lg text-[13px] transition-colors ${
                  paginaAtiva === i
                    ? 'bg-[#F1EAFE] dark:bg-violet-500/10 text-[#5B21B6] dark:text-violet-300 font-medium'
                    : 'text-gray-600 dark:text-[#ABABAD] hover:bg-[#F6F5FA] dark:hover:bg-[#383838]/50'
                }`}
              >
                <Iconify icon="mdi:file-outline" width={14} />
                Página {i + 1}
              </button>
              {paginas.length > 1 && (
                <button
                  type="button"
                  onClick={() => onRemovePagina(i)}
                  className="opacity-0 group-hover:opacity-100 focus-visible:opacity-100 p-1 rounded-md text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-500/10 transition-all"
                  title="Remover página"
                  aria-label={`Remover página ${i + 1}`}
                >
                  <Iconify icon="mdi:trash-can-outline" width={13} />
                </button>
              )}
            </div>
          ))}
        </div>
        <button type="button" onClick={onAddPagina} className={`${D.addBlock} w-full justify-center mt-1 text-[13px]`}>
          <Iconify icon="eva:plus-fill" width={14} />
          Nova página
        </button>
      </div>

      <p className={`${D.paletteHint} pt-3 border-t border-gray-200/70 dark:border-[#383838]`}>
        Duplo clique num texto para editar. Setas movem, Delete remove, Ctrl+D duplica, Ctrl+Z desfaz.
      </p>
    </div>
  );
}
